<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Device Configurations Scheduling</h1>
                <v-btn :disabled="!company" color="black"
                       prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="4" sm="6">
                <company-select v-model="company" :items="companies" density="compact" label="Select Company"
                          @update:model-value="fetchSchedules"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn??null" column="id"
                                sortable title="ID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="label"
                                sortable title="Label" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="updated_at"
                                sortable title="Last Updated" @sort="sort"/>
                        <th-btn v-if="user.type === 'admin'" :sorted-column="sortedColumn??null" column="type"
                                sortable title="Type" @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="schedule in schedules" v-if="schedules.length" :key="schedule.id" class="text-left">
                        <td>{{ schedule.id }}</td>
                        <td>{{ schedule.label }}</td>
                        <td>{{ schedule.updated_at ? formatDateTime(schedule.updated_at) : '' }}</td>
                        <td v-if="user.type === 'admin'">{{ schedule.type === 1 ? 'Default' : 'Non Default' }}</td>
                        <td>
                            <v-btn-group v-if="schedule.type > 1" density="compact" divided variant="outlined">
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editSchedule(schedule)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete"
                                       size="x-small"
                                       @click="deleteSchedule(schedule)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="3">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="3">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchSchedules"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <Create-Schedule v-if="createDialogSwitch" v-model="createDialogSwitch" :company-id="company"
                     @addSchedule="addSchedule"/>
    <Edit-Schedule v-if="editDialogSwitch" v-model="editDialogSwitch" :schedule="selectedSchedule"
                   @updateSchedule="updateSchedule"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import ThBtn from "@/components/globals/th-btn.vue";
import EditSchedule from "@/components/dialogs/monitoring/schedules/Edit.vue";
import CreateSchedule from "@/components/dialogs/monitoring/schedules/Create.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const company = ref((user.type !== 'admin') ? user.company.id : null);
const companies = ref([]);
const schedules = ref([]);
const loading = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref('desc');
const sortedColumn = ref('updated_at');
const selectedSchedule = ref(null);
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    }
    fetchSchedules();
});
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchSchedules();
}
const fetchSchedules = () => {
    if (company.value) {
        loading.value = true;
        send('get', `monitoring/time-schedules`, {
            page: currentPage.value,
            company: company.value,
            sort: sortedColumn.value,
            order: sortedType.value
        })
            .then((response) => {
                loading.value = false;
                schedules.value = response.data.data.data;
                totalPages.value = response.data.data.last_page;
                currentPage.value = response.data.data.current_page;
            })
            .catch((error) => {
                console.log(error)
                loading.value = false;
                showSnackbar('Asset Schedules could not be loaded', 'error')
            });
    }
}
const addSchedule = (schedule) => {
    schedules.value.unshift(schedule);
}
const editSchedule = (schedule) => {
    selectedSchedule.value = schedule;
    editDialogSwitch.value = true;
}
const updateSchedule = (schedule) => {
    schedules.value.map((sch, index) => {
        if (sch.id === schedule.id) {
            schedules.value[index] = schedule;
        }
    });
}
const deleteSchedule = (schedule) => {
    if (confirm(`Are you sure to delete "${schedule.label}" schedule?`)) {
        send('delete', `monitoring/time-schedules/${schedule.id}`)
            .then((response) => {
                if (response.data.success) {
                    schedules.value.map((item, index) => {
                        if (item.id === schedule.id) {
                            schedules.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${schedule.title}" Schedule could not be deleted`, 'error')
                }
            })
    }
}
</script>
